import {
    AfterViewInit,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { finalize, map, Observable, Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { DirectieSeriiService } from './directie-serii.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { AddSerieComponent } from './add-serie/add-serie.component';
import { DirectieSerii } from './directie-serii.interface';
import { EditPrefixComponent } from './edit-prefix/edit-prefix.component';
import { MassAssignStatusComponent } from './mass-assign-status/mass-assign-status.component';
import { AlocareResponsabileCompartimentComponent } from './alocare-responsabile-compartiment/alocare-responsabile-compartiment.component';

@Component({
    selector: 'app-directie-serii',
    templateUrl: './directie-serii.component.html',
    styleUrls: ['./directie-serii.component.scss']
})
export class DirectieSeriiComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    dgaspc_id = environment.interop.dgaspcId;

    isLoading = false;
    showCard = false;
    directie_id: number = this.dgaspc_id;
    activeValue: string = '-1';
    publicValue: string = '-1';
    blocareValue: string = '-1';

    directii$!: Observable<Generic[]>;

    isFilterLoading = false;

    defaultParameter: { name: string; id: number } = {
        name: 'Toate',
        id: -1,
    };

    checkboxSelections = false;
    selectedRowIds: string[] = [];
    rowCount = 0;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink = this.requestService.listDTUrl;

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    resetForm() {
        this.activeValue = '-1';
        this.publicValue = '-1';

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private dialog: MatDialog,
        private requestService: DirectieSeriiService,
        public permissionsService: PermissionsService,
        private commonService: CommonService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.getActiveDirectii();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    // Track expanded rows for showing all functions
    expandedResponsables: Set<number> = new Set();

    // Check if row is expanded
    isRowResponsableExpanded(userId: number): boolean {
        return this.expandedResponsables.has(userId);
    }

    // Toggle expanded state for a row
    toggleFunctiiExpansion(userId: number): void {
        if (this.expandedResponsables.has(userId)) {
            this.expandedResponsables.delete(userId);
        } else {
            this.expandedResponsables.add(userId);
        }
    }

    renderDT() {
        const self = this;

        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.active = this.activeValue;
                dataTablesParameters.public_view = this.publicValue;
                dataTablesParameters.blocked = this.blocareValue;
                dataTablesParameters.directie_id = this.directie_id;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        this.getTableRowCount();
                        this.checkboxesReset();

                        const processedData = this.processTableData(resp.data.data);

                        // Event delegation for the labels with data-action="deleteSerie" attribute 
                        const tableElement = document.querySelector('.table-container');
                        if (tableElement) {
                            tableElement.addEventListener('click', (event: Event) => {
                                const target = event.target as HTMLElement;
                                const actionElement = target.closest('[data-action="deleteSerie"]') as HTMLElement;

                                if (actionElement) {
                                    const directieId = Number(actionElement.getAttribute('data-directie-id'));
                                    const compartimentId = Number(actionElement.getAttribute('data-compartiment-id'));
                                    const sequenceId = Number(actionElement.getAttribute('data-sequence-id'));

                                    this.deleteSerie(directieId, compartimentId, sequenceId);
                                }
                            });
                        }

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: processedData,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: `<input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" id="headerCheckbox"/>`,
                    data: null,
                    orderable: false,
                    className: "all",
                    width: "5%", // Add width here
                    render: (data: any, type: any, row: any) => {
                        return (
                            `
							<div style="display: flex; align-items: center; gap: 5px;">
							    <input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" value="${row.index}" />
                            </div>
                            `
                        );
                    },
                },
                {
                    title: 'Direcție',
                    data: 'directie_denumire',
                    width: "20%", // Add width here
                },
                {
                    title: 'Compartiment',
                    data: 'compartiment_denumire',
                    width: "20%", // Add width here
                },
                {
                    title: 'Serie aplicație',
                    data: 'sequence_labels',
                    width: "20%", // Add width here
                },
                {
                    title: 'Prefix',
                    data: 'prefix',
                    width: "5%", // Add width here
                },
                {
                    title: 'Responsabili',
                    data: 'assigned_users_names',
                    width: "20%", // Add width here
                    orderable: false,
                    render: (data: any[], type: string, row: any) => {

                        let html = '';
                        html += '<div class="inactive-functions mt-2">';
                        data.forEach(responsable => {
                            html += `<div class="inactive-function text-muted small">
                                        <i class="fa fa-user me-1"></i> ${responsable}
                                        </div>`;
                        });
                        html += '</div>';

                        return html;
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    width: "10%", // Add width here
                    render: (row: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                        <button class="action-btn allocate ms-1" title="Adăugare serie">
                            <img class="btn-img" src="../../../../assets/icons/add.svg" alt="add_serie"/>
                        </button>
                        <button class="action-btn edit ms-1" title="Editare prefix">
                            <img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
                        </button>
                        <button class="action-btn action-danger users ms-1" title="Alocare responsabili">
                            <img class="btn-img" src="../../../../assets/icons/work_outline.svg" alt="users"/>
                        </button>
                        <button type="button" class="action-btn update_status_active ms-1 ` +
                            (row.active == 1 ? 'action-success' : 'action-danger') +
                            `" title="Schimbare status activ">
                                <img class="btn-img" src="` +
                            (row.active == 1
                                ? '/assets/icons/check-white.svg'
                                : '/assets/icons/close-white.svg') +
                            `" alt="update_status_active"/>
                        </button>
                        <button type="button" class="action-btn update_status_public ms-1 ` +
                            (row.public_view == 1
                                ? 'action-success'
                                : 'action-danger') +
                            `" title="Schimbare status public">
                            <img class="btn-img" src="` +
                            (row.public_view == 1
                                ? '/assets/icons/public_white.svg'
                                : '/assets/icons/public_not_white.svg') +
                            `" alt="update_status_public"/>
                        </button>
                        <button type="button" class="action-btn new_numbers_block ms-1 ` +
                            (row.blocked == true
                                ? 'action-success'
                                : 'action-danger') +
                            `" title="Blocare/Deblocare">
                            <img class="btn-img" src="/assets/icons/block-white.svg" alt="update_status_blocare"/>
                        </button>
                    </div>
            `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');

                // Handle click event on the CHECKBOXES
                $(row)
                    .find('.row-checkbox')
                    .off('change')
                    .on('change', function () {
                        let index = data.index;
                        console.log(index);

                        if ($(this).is(':checked')) {
                            self.selectedRowIds.push(index);
                        } else {
                            self.selectedRowIds = self.selectedRowIds.filter(
                                (id) => id !== index
                            );
                        }

                        if (self.rowCount !== self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(false);
                        } else if (self.rowCount === self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(true);
                        }

                        self.checkboxSelections = self.selectedRowIds.length > 0;
                    });

                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const allocateIcon = lastTd?.querySelector('.allocate') as HTMLElement;
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const usersIcon = lastTd?.querySelector('.users') as HTMLElement;
                // const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const updateStatusActiveIcon = lastTd?.querySelector(
                    '.update_status_active'
                ) as HTMLElement;
                const updateStatusPublicIcon = lastTd?.querySelector(
                    '.update_status_public'
                ) as HTMLElement;
                const updateStatusBlocareIcon = lastTd?.querySelector(
                    '.new_numbers_block'
                ) as HTMLElement;

                if (allocateIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.update-serie'
                        )
                    ) {
                        allocateIcon.style.display = 'none';
                    }
                    allocateIcon.addEventListener('click', () => {
                        this.openAddSerieDialog(data);
                    });
                }

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditPrefixDialog(data);
                    });
                }

                if (usersIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.update'
                        )
                    ) {
                        usersIcon.style.display = 'none';
                    }
                    usersIcon.addEventListener('click', () => {
                        this.openAlocareResponsabileDialog(data);
                    });
                }

                // if (deleteIcon) {
                //     // if (
                //     //     !this.permissionsService.hasPermission(
                //     //         'indoc-module.settings.statuses.delete'
                //     //     )
                //     // ) {
                //     //     deleteIcon.style.display = 'none';
                //     // }
                //     deleteIcon.addEventListener('click', () => {
                //         this.deleteSerie(data);
                //     });
                // }

                if (updateStatusActiveIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.update-status'
                        )
                    ) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusActiveIcon.addEventListener('click', () => {
                        const { directie_id, id_compartiment } = data;
                        this.changeStatusActive(directie_id, id_compartiment);
                    });
                }

                if (updateStatusPublicIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.update-status-public'
                        )
                    ) {
                        updateStatusPublicIcon.style.display = 'none';
                    }
                    updateStatusPublicIcon.addEventListener('click', () => {
                        const { directie_id, id_compartiment } = data;
                        this.changeStatusPublic(directie_id, id_compartiment);
                    });
                }

                if (updateStatusBlocareIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.institutii-servicii-registre-active.block'
                        )
                    ) {
                        updateStatusBlocareIcon.style.display = 'none';
                    }
                    updateStatusBlocareIcon.addEventListener('click', () => {
                        const { directie_id, id_compartiment } = data;
                        this.changeStatusBlocare(directie_id, id_compartiment);
                    });
                }

                return row;
            },
            headerCallback: (row: Node, data: any[] | any) => {
                const headerCheckbox = $(row).find('#headerCheckbox');

                // Remove any existing event handlers to avoid duplicating them
                headerCheckbox.off('change click');

                // Handle the change event for selecting/deselecting all checkboxes
                headerCheckbox.on('change', function () {
                    self.selectedRowIds.length = 0;
                    if ($(this).is(':checked')) {
                        data.forEach((item: DirectieSerii) => {
                            self.selectedRowIds.push(item.index);
                        });
                        self.checkMultipleCheckboxesByValue(true);
                    } else {
                        self.checkMultipleCheckboxesByValue(false);
                    }
                    self.checkboxSelections = self.selectedRowIds.length > 0;
                    console.log('self.checkboxSelections', self.checkboxSelections);
                });

                // Prevent sorting when clicking the checkbox
                headerCheckbox.on('click', function (e) {
                    e.stopPropagation();
                });

                return row;
            },
        };
    }

    async getTableRowCount() {
        const table = await this.dtElement.dtInstance;
        this.rowCount = table.rows().count();
    }

    checkHeaderCheckBox(checkValue: boolean) {
        const headerCheckbox = document.getElementById(
            "headerCheckbox"
        ) as HTMLInputElement;
        headerCheckbox.checked = checkValue;
    }
    checkMultipleCheckboxesByValue(checkValue: boolean) {
        let checkboxes = document.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"]'
        );
        checkboxes.forEach((checkbox) => {
            checkbox.checked = checkValue;
        });
    }
    checkboxesReset() {
        this.selectedRowIds.length = 0;
        this.checkMultipleCheckboxesByValue(false);
        this.checkHeaderCheckBox(false);
        this.checkboxSelections = false;
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
        this.showCard = false;
    }

    transformSequenceLabels(sequenceLabelsHtml: string, directieId: number, compartimentId: number): string {
        const hasDeletePermission = this.permissionsService.hasPermission(
            'indoc-module.settings.institutii-servicii-registre-active.update-serie'
        );

        if (!hasDeletePermission) {
            // Elimină iconița X, background-ul roșu, liniuța și atributele de click
            return sequenceLabelsHtml
                .replace(/ng-click='vm\.removeSerie\([^)]+\)'/g, '') // Elimină ng-click
                .replace(/<i[^>]*class=['"][^'"]*fa\s+fa-times[^'"]*['"][^>]*><\/i>/g, '') // Elimină iconița X
                .replace(/style='[^']*cursor:\s*pointer[^']*'/g, '') // Elimină cursor pointer
                .replace(/label-danger/g, 'label-default') // Schimbă background-ul roșu cu gri
                .replace(/>\s*-\s*/g, '>') // Elimină " - " de după >
                .trim();
        }

        // Dacă are permisiune, transformă ng-click în data attributes și elimină tot liniuța
        return sequenceLabelsHtml
            .replace(/>\s*-\s*/g, '>') // Elimină " - " de după >
            .replace(
                /ng-click='vm\.removeSerie\((\d+),(\d+),\s*(\d+)\)'/g,
                (match, dir, comp, seq) =>
                    `data-action='deleteSerie' data-directie-id='${directieId}' data-compartiment-id='${compartimentId}' data-sequence-id='${seq}' style='cursor: pointer;'`
            );
    }

    processTableData(data: DirectieSerii[]) {
        return data.map(item => ({
            ...item,
            sequence_labels: this.transformSequenceLabels(
                item.sequence_labels,
                item.directie_id,
                item.id_compartiment
            )
        }));
    }

    getActiveDirectii() {
        this.isFilterLoading = true;
        this.directii$ = this.commonService
            .getDirectiiActive()
            .pipe(
                finalize(() => (this.isFilterLoading = false)),
                map((resp: Generic[]) => [this.defaultParameter, ...resp])
            );
    }

    openAddSerieDialog(data: DirectieSerii) {
        const dialogRef = this.dialog.open(AddSerieComponent, {
            ...this.dialogService.getDialogConfig(),
            minHeight: '200px',
            data,
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Serie adăugată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditPrefixDialog(data: DirectieSerii) {
        const dialogRef = this.dialog.open(EditPrefixComponent, {
            ...this.dialogService.getDialogConfig(),
            minHeight: '200px',
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Prefix actualizat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openAlocareResponsabileDialog(data: DirectieSerii) {
        const dialogRef = this.dialog.open(AlocareResponsabileCompartimentComponent, {
            ...this.dialogService.getDialogConfig(),
            minHeight: '400px',
            data: {
                directie_id: data.directie_id,
                compartiment_id: data.id_compartiment
            }
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Responsabil adăugat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteSerie(directieId: number, compartimentId: number, sequenceId: number) {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: 'O să ștergeți această serie',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.requestService.removeSerie(directieId, compartimentId, sequenceId).subscribe({
                    next: () => {
                        this.rerenderDT();
                        Swal.fire({
                            title: this.helper.swalTextConfig.headers.deleted,
                            text: 'Serie ștearsă cu succes.',
                            icon: 'success',
                            confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                        });
                    },
                    error: (err: any) => {
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: err.error.errors.message[0],
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        })
                    }
                });
            }
        });
    }

    changeStatusActive(directieId: number, compartimentId: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusActiveAction(directieId, compartimentId);
            }
        });
    }

    changeStatusActiveAction(directieId: number, compartimentId: number) {
        this.isLoading = true;
        this.requestService.changeStatusActive(directieId, compartimentId).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: any) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusActiveAction(directieId, compartimentId);
                    }
                });
            }
        );
    }

    changeStatusPublic(directieId: number, compartimentId: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusPublicAction(directieId, compartimentId);
            }
        });
    }

    changeStatusPublicAction(directieId: number, compartimentId: number) {
        this.isLoading = true;
        this.requestService.changeStatusPublic(directieId, compartimentId).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusPublicAction(directieId, compartimentId);
                    }
                });
            }
        );
    }

    openChangeStatusComponent(type: string) {
        const data = { obj_list: this.selectedRowIds, type }
        const dialogRef = this.dialog.open(MassAssignStatusComponent, {
            ...this.dialogService.getDialogConfig(),
            minHeight: '200px',
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Status actualizat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    changeStatusBlocare(directieId: number, compartimentId: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul de blocare al înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusBlocareAction(directieId, compartimentId);
            }
        });
    }

    changeStatusBlocareAction(directieId: number, compartimentId: number) {
        this.isLoading = true;
        this.requestService.changeStatusBlocare(directieId, compartimentId).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: any) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusBlocareAction(directieId, compartimentId);
                    }
                });
            }
        );
    }
}

